import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { SignUpHeader } from '../components/SignUpHeader';
import { FloatingLabelInput } from '../components/FloatingLabelInput';
import { PrimaryButton } from '../components/PrimaryButton';
import { SubscriptionPlanOffcanvas } from '../components/SubscriptionPlanOffcanvas';
import {
  customerApiErrorMessage,
  signup,
} from '../../lib/customer-api';
import { setCustomerAuth } from '../../lib/customer-auth';
import { useBookingDraft } from '../../lib/booking-draft';
import { useCustomerAuth } from '../../lib/use-customer-auth';
import { toast } from '../../lib/toast';
import { sanitizeName } from '../../lib/validators';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NewToCherryPage() {
  const navigate = useNavigate();
  const draft = useBookingDraft();
  const { isAuthenticated } = useCustomerAuth();

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });
  const [errors, setErrors] = useState<{
    email?: string;
    firstName?: string;
    lastName?: string;
    password?: string;
    confirmPassword?: string;
    terms?: string;
    address?: string;
  }>({});
  const [submitting, setSubmitting] = useState(false);
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);

  const update = (key: keyof typeof formData, value: any) => {
    setFormData((p) => ({ ...p, [key]: value }));
    if (errors[key as keyof typeof errors]) {
      setErrors((p) => ({ ...p, [key]: undefined }));
    }
  };

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (!formData.email.trim()) next.email = 'Email is required.';
    else if (!EMAIL_RE.test(formData.email.trim()))
      next.email = 'Enter a valid email address.';
    if (!formData.firstName.trim()) next.firstName = 'First name is required.';
    if (!formData.lastName.trim()) next.lastName = 'Last name is required.';
    if (!formData.password) next.password = 'Password is required.';
    else if (formData.password.length < 8 || !/\d/.test(formData.password))
      next.password = 'Use at least 8 characters and include a number.';
    if (formData.confirmPassword !== formData.password)
      next.confirmPassword = 'Passwords do not match.';
    if (!formData.agreeToTerms)
      next.terms = 'You must accept the terms to continue.';
    // We can't sign up without a UK address — that's a backend
    // requirement. The booking flow captures it on the landing page,
    // so a missing address here means the user nav'd here directly.
    const addr = draft?.address;
    if (!addr?.address_line1 || !addr?.postcode || addr.latitude == null || addr.longitude == null) {
      next.address =
        'We need a delivery address to create your account. Please start your booking from the home page.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSignUp = async () => {
    if (!validate()) return;
    const addr = draft?.address;
    if (!addr) return; // validate() already flagged this
    setSubmitting(true);
    try {
      const res = await signup({
        email: formData.email.trim().toLowerCase(),
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        password: formData.password,
        accept_terms: true,
        address_line1: addr.address_line1!,
        postcode: addr.postcode!,
        latitude: addr.latitude!,
        longitude: addr.longitude!,
        address_line2: addr.address_line2 ?? null,
        city: addr.city ?? null,
        country_code: addr.country_code ?? null,
      });
      setCustomerAuth(res.accessToken, res.user);
      toast.success('Account created successfully!');
      // This page only exists inside the booking flow (it redirects
      // authed visitors to /continue-booking above), so a fresh signup
      // here must resume checkout — not drop the customer on the
      // homepage and lose the basket they were building.
      navigate('/continue-booking');
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Sign-up failed.');
      // Backend rejects duplicate emails — surface that on the email field.
      if (/email/i.test(msg)) {
        setErrors((p) => ({ ...p, email: msg }));
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // This page is the guest sign-up step. A signed-in customer doesn't
  // need it — send them straight on to confirm their booking. (Placed
  // after all hooks so hook order stays stable.)
  if (isAuthenticated) {
    return <Navigate to="/continue-booking" replace />;
  }

  const handleCloseSubscription = () => setIsSubscriptionOpen(false);
  const handlePickPlan = () => {
    setIsSubscriptionOpen(false);
    navigate('/continue-booking');
  };
  const handleSkip = () => {
    setIsSubscriptionOpen(false);
    navigate('/continue-booking');
  };

  return (
    <div className="min-h-screen bg-white">
      <SignUpHeader showBackButton={true} />

      <main className="flex flex-col items-center pt-12 pb-24 px-4">
        <div className="text-center mb-12">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] !text-[25px] md:!text-[34px] leading-[32px] md:leading-[48px] mb-2">
            New to Cherry Picked?
          </h1>
          <h2 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] leading-[36px]">
            Register to continue
          </h2>
        </div>

        <div className="w-full max-w-[420px] flex flex-col gap-6">
          <FloatingLabelInput
            label="Email"
            type="email"
            value={formData.email}
            onChange={(val) => update('email', val)}
            icon={``}
            className="w-full"
            error={!!errors.email}
            errorMessage={errors.email}
          />

          <div className="flex gap-4">
            <FloatingLabelInput
              label="First name *"
              type="text"
              value={formData.firstName}
              onChange={(val) => update('firstName', sanitizeName(val))}
              icon={``}
              className="w-1/2"
              error={!!errors.firstName}
              errorMessage={errors.firstName}
            />
            <FloatingLabelInput
              label="Last name *"
              type="text"
              value={formData.lastName}
              onChange={(val) => update('lastName', sanitizeName(val))}
              icon={``}
              className="w-1/2"
              error={!!errors.lastName}
              errorMessage={errors.lastName}
            />
          </div>

          <FloatingLabelInput
            label="Password"
            type="password"
            value={formData.password}
            onChange={(val) => update('password', val)}
            icon={``}
            className="w-full"
            error={!!errors.password}
            errorMessage={errors.password}
          />

          <FloatingLabelInput
            label="Confirm password"
            type="password"
            value={formData.confirmPassword}
            onChange={(val) => update('confirmPassword', val)}
            icon={``}
            className="w-full"
            error={!!errors.confirmPassword}
            errorMessage={errors.confirmPassword}
          />

          <div>
            <div className="flex items-center gap-[10px]">
              <button
                type="button"
                onClick={() => update('agreeToTerms', !formData.agreeToTerms)}
                className={`size-[20px] rounded-full flex items-center justify-center transition-colors border ${
                  formData.agreeToTerms
                    ? 'bg-[#8e2a2f] border-[#8e2a2f]'
                    : 'bg-white border-[#eee]'
                }`}
              >
                {formData.agreeToTerms && (
                  <span className="text-white text-[12px]">✓</span>
                )}
              </button>
              <p className="font-['DM_Sans',sans-serif] font-bold leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
                I agree with terms &amp; conditions
              </p>
            </div>
            {errors.terms ? (
              <p className="mt-1 ml-[30px] text-[#991b1b] font-['DM_Sans',sans-serif] text-[12px]">
                {errors.terms}
              </p>
            ) : null}
          </div>

          {errors.address ? (
            <p className="text-[#991b1b] font-['DM_Sans',sans-serif] text-[14px] -mt-2">
              {errors.address}
            </p>
          ) : null}

          <PrimaryButton
            onClick={handleSignUp}
            className="w-full py-4"
            disabled={submitting}
          >
            {submitting ? 'Creating account…' : 'Sign Up'}
          </PrimaryButton>
        </div>
      </main>

      <SubscriptionPlanOffcanvas
        isOpen={isSubscriptionOpen}
        onClose={handleCloseSubscription}
        onPickPlan={handlePickPlan}
        onSkip={handleSkip}
        showArrow={true}
      />
    </div>
  );
}
